/**
 * CPV-AI-01 · one assistant answer, as the server stored it (PRD §4.1, §8.1, §11 screens 1–3). Used by the workspace's
 * thread and by the "Explain this trend" / "Summarise signal" drawer.
 *
 *   the label (a quiet line), the summary, the findings — each marked fact or hypothesis, with its evidence chips that
 *   open the source record — the hypotheses (not verified), the limitations and the questions to review; then copy,
 *   open source and helpful / not helpful with a comment. Engine, model, draft status and the context answered sit
 *   behind the "i". Nothing is computed here: every word and every link is the server's.
 *
 * Also: AnswerPending (progress while the server answers, §9), AnswerRefused (the server's refusal, verbatim); EvidenceChip,
 * Section and Feedback are shared with the report assistant's draft (ReportDraftAnswer).
 */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Input, Progress } from 'antd';
import { Copy, ExternalLink, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AlertBox, ASSISTANT, Chip, InfoHint } from '@/components/cpv-ui';
import { ACTION, EVIDENCE_UNAVAILABLE, FINDING, answerState, answerText, assistantApi } from './assistantApi';
import { refusalOf } from '../cpvApi';
import './assistant.css';

const FEEDBACK_ICON = { [ACTION.HELPFUL]: ThumbsUp, [ACTION.NOT_HELPFUL]: ThumbsDown };
/** The draft status in plain words (engines/vocabulary/cpv_ai.py DraftStatus). */
const DRAFT_WORDS = { ASSISTANT_DRAFT: 'Draft, not reviewed' };

/** Only an in-app route is ever opened: "/…" and never "//host" (a scheme such as javascript: or https: is not a route). */
const INTERNAL = /^\/(?!\/)/;
export const isInternalLink = (link) => typeof link === 'string' && INTERNAL.test(link);

/** Open a reference in the app; anything that is not an in-app route is never opened. */
export function useOpenLink() {
  const navigate = useNavigate();
  return (link) => { if (isInternalLink(link)) navigate(link); };
}

export function EvidenceChip({ ev, onOpen }) {
  if (!isInternalLink(ev.deep_link)) {
    return (
      <span className="ca-ev ca-ev--text" data-testid={`ca-ev-${ev.key}`}>
        <span className="ca-ev-key">{ev.key}</span>
        <span className="ca-ev-label">{ev.display_label}</span>
      </span>
    );
  }
  return (
    <button type="button" className="ca-ev" onClick={() => onOpen(ev.deep_link)} title={`Open ${ev.display_label}`}
      data-testid={`ca-ev-${ev.key}`}>
      <span className="ca-ev-key">{ev.key}</span>
      <span className="ca-ev-label">{ev.display_label}</span>
      <ExternalLink size={ASSISTANT.iconSm} aria-hidden />
    </button>
  );
}
EvidenceChip.propTypes = { ev: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

export function Section({ title, children, testId }) {
  return (
    <section className="ca-section" data-testid={testId}>
      <h3 className="ca-section-title">{title}</h3>
      {children}
    </section>
  );
}
Section.propTypes = { title: PropTypes.node, children: PropTypes.node, testId: PropTypes.string };

/** Helpful / not helpful, each with an optional comment; the server's words and the person's own last choice. */
export function Feedback({ response, onChange }) {
  const [picked, setPicked] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const mine = [...(response.review_actions || [])].reverse().find((x) => x.mine);
  const send = async () => {
    setBusy(true); setRefusal(null);
    try {
      const next = await assistantApi.feedback(response.id, picked, comment.trim());
      setPicked(null); setComment('');
      onChange?.(next);
      toast.success('Thank you. Your feedback is recorded.');
    } catch (e) { setRefusal(refusalOf(e, 'The feedback could not be recorded.')); }
    setBusy(false);
  };
  return (
    <div className="ca-feedback" data-testid="ca-feedback">
      <div className="ca-actions-row">
        {(response.feedback_actions || []).map((a) => {
          const Icon = FEEDBACK_ICON[a.action];
          const on = picked === a.action || (!picked && mine?.action === a.action);
          return (
            <Button key={a.action} size="small" type={on ? 'primary' : 'default'} aria-pressed={on}
              icon={Icon ? <Icon size={ASSISTANT.icon} aria-hidden /> : null}
              onClick={() => setPicked(picked === a.action ? null : a.action)} data-testid={`ca-feedback-${a.action}`}>
              {a.words}
            </Button>
          );
        })}
        {mine && !picked ? <span className="cu-muted cu-small" data-testid="ca-feedback-mine">You marked this {mine.words.toLowerCase()}</span> : null}
      </div>
      {picked && (
        <div className="ca-feedback-form">
          <Input.TextArea rows={ASSISTANT.promptRows - 1} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment (optional)" aria-label="Feedback comment" data-testid="ca-feedback-comment" />
          <div className="ca-actions-row">
            <Button size="small" type="primary" loading={busy} onClick={send} data-testid="ca-feedback-send">Send feedback</Button>
            <Button size="small" onClick={() => { setPicked(null); setComment(''); }}>Cancel</Button>
          </div>
        </div>
      )}
      {refusal && <AlertBox tone="error" title={refusal.code} testId="ca-feedback-refusal">{refusal.message}</AlertBox>}
    </div>
  );
}
Feedback.propTypes = { response: PropTypes.object.isRequired, onChange: PropTypes.func };

export default function AssistantAnswer({ response, onChange, testId = 'ca-answer' }) {
  const open = useOpenLink();
  const a = response.answer || {};
  const state = answerState(response);
  const byKey = Object.fromEntries((response.evidence || []).map((e) => [e.key, e]));
  // a limitation that only repeats the summary (an unsupported or empty answer) is not shown twice
  const limitations = (a.limitations || []).filter((l) => l !== a.summary);
  const copy = async () => {
    try { await navigator.clipboard.writeText(answerText(response)); toast.success('Answer copied.'); }
    catch { toast.error('The answer could not be copied.'); }
  };
  const sources = (response.evidence || []).filter((e) => isInternalLink(e.deep_link)).map((e) => ({ key: e.key, label: `${e.key} · ${e.display_label}`, onClick: () => open(e.deep_link) }));
  return (
    <article className="ca-answer" data-testid={testId} data-state={state} aria-label="Assistant answer">
      <div className="ca-label" data-testid="ca-label">
        <span>{response.label}</span>
        {/* the engine and model stay in the server's audit record; the reader sees a neutral line and the draft status */}
        <InfoHint label="Answer details" title="About this answer" testId="ca-answer-info" placement="bottomRight"
          facts={[['Status', DRAFT_WORDS[response.draft_status] || response.draft_status]]}>
          Answered by the CPV assistant.
        </InfoHint>
      </div>

      {state === 'unsupported' && (
        <AlertBox tone="neutral" title="Not a question the assistant answers yet" testId="ca-unsupported">{a.summary}</AlertBox>
      )}
      {state === 'insufficient' && (
        <AlertBox tone="warning" title="Not enough data to answer" testId="ca-insufficient">{a.summary}</AlertBox>
      )}
      {state === 'answer' && <p className="ca-summary" data-testid="ca-summary">{a.summary}</p>}

      {(a.findings || []).length > 0 && (
        <Section title="Findings" testId="ca-findings">
          <ul className="ca-list">
            {a.findings.map((f, i) => {
              const fact = f.type === FINDING.FACT;
              const refs = (f.evidence || []).map((k) => byKey[k]).filter(Boolean);
              return (
                <li key={i} className="ca-finding" data-testid={`ca-finding-${i}`}>
                  <Chip tone={fact ? 'info' : 'accent'}>{fact ? 'Fact' : 'Hypothesis'}</Chip>
                  <div className="ca-finding-body">
                    <div>{f.text}</div>
                    {(refs.length > 0 || f.evidence_status === EVIDENCE_UNAVAILABLE) && (
                      <div className="ca-evs">
                        {refs.map((ev) => <EvidenceChip key={ev.key} ev={ev} onOpen={open} />)}
                        {f.evidence_status === EVIDENCE_UNAVAILABLE
                          ? <Chip tone="warning" title="The source record is outside your access or has changed">Evidence unavailable</Chip> : null}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {(a.hypotheses || []).length > 0 && (
        <Section title="Hypotheses" testId="ca-hypotheses">
          <ul className="ca-list">
            {a.hypotheses.map((h, i) => (
              <li key={i} className="ca-finding"><Chip tone="caution">Not verified</Chip><div className="ca-finding-body">{h.text}</div></li>
            ))}
          </ul>
        </Section>
      )}

      {limitations.length > 0 && (
        <Section title="Limitations" testId="ca-limitations">
          <ul className="ca-bullets cu-muted">{limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </Section>
      )}

      {(a.review_questions || []).length > 0 && (
        <Section title="Questions to review" testId="ca-questions">
          <ul className="ca-bullets">{a.review_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </Section>
      )}

      <footer className="ca-answer-foot">
        <div className="ca-actions-row">
          <Button size="small" icon={<Copy size={ASSISTANT.icon} aria-hidden />} onClick={copy} data-testid="ca-copy">Copy answer</Button>
          {sources.length === 1
            ? <Button size="small" icon={<ExternalLink size={ASSISTANT.icon} aria-hidden />} onClick={sources[0].onClick} data-testid="ca-open-source">Open source</Button>
            : (
              <Dropdown disabled={!sources.length} trigger={['click']} menu={{ items: sources.map(({ key, label, onClick }) => ({ key, label, onClick })) }}>
                <Button size="small" icon={<ExternalLink size={ASSISTANT.icon} aria-hidden />} disabled={!sources.length} data-testid="ca-open-source">Open source</Button>
              </Dropdown>
            )}
        </div>
        <Feedback response={response} onChange={onChange} />
      </footer>
    </article>
  );
}
AssistantAnswer.propTypes = { response: PropTypes.object.isRequired, onChange: PropTypes.func, testId: PropTypes.string };

/** Progress while the server answers: the seconds waited against the PRD's target (§9: under 10 s for common questions). */
export function AnswerPending({ since, testId = 'ca-pending' }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ASSISTANT.tickMs); return () => clearInterval(t); }, []);
  const seconds = Math.max(0, Math.floor((now - since) / ASSISTANT.tickMs));
  const share = Math.min(seconds / ASSISTANT.targetSeconds, 1);
  return (
    <div className="ca-pending" role="status" data-testid={testId}>
      <div className="ca-pending-line">
        <span>Reading the CPV records and checking the evidence…</span>
        <span className="cu-muted cu-small">{seconds} s</span>
      </div>
      <Progress percent={share * 100} showInfo={false} status="active" size="small" />
      {seconds >= ASSISTANT.targetSeconds
        ? <div className="cu-muted cu-small">This is taking longer than usual. You can keep waiting.</div>
        : <div className="cu-muted cu-small">Most answers take under {ASSISTANT.targetSeconds} seconds.</div>}
    </div>
  );
}
AnswerPending.propTypes = { since: PropTypes.number.isRequired, testId: PropTypes.string };

/** The server's refusal, verbatim, with a retry. */
export function AnswerRefused({ refusal, onRetry, testId = 'ca-error' }) {
  return (
    <AlertBox tone="error" title="The assistant could not answer" testId={testId}>
      <div data-testid="ca-error-message">{refusal.message}</div>
      <div className="cu-muted cu-small">{refusal.code}</div>
      {onRetry ? <Button size="small" className="ca-retry" onClick={onRetry} data-testid="ca-retry">Try again</Button> : null}
    </AlertBox>
  );
}
AnswerRefused.propTypes = { refusal: PropTypes.object.isRequired, onRetry: PropTypes.func, testId: PropTypes.string };
